// @ts-strict-ignore
import { DOCUMENT } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  HostListener,
  Inject,
  Input,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { DomSanitizer, SafeStyle } from '@angular/platform-browser';
import * as echarts from 'echarts';
import { ECharts, EChartsOption } from 'echarts';
import deepEqual from 'fast-deep-equal/es6';
import { Subject, debounceTime } from 'rxjs';
import { chartUnitToUIString } from '../../../../../functions/charts/chart-unit-to-ui-string';
import { UI_STRINGS } from '../../../../../functions/language/uistrings';
import { numberOfDecimalsFromValue } from '../../../../../functions/numbers/number-of-decimals-from-value';
import { round } from '../../../../../functions/numbers/round-number';
import { ChartUnit } from '../../../../../model/chart-unit';
import { CCCModelUtility } from '../../../../../services/climate-engine/ccc-model-utility/ccc-model-utility';
import { FormatNumberService } from '../../../../../services/format-number.service';
import { TranslationService } from '../../../../../services/translation-service';
import { UnitConversionService } from '../../../../../services/unit-conversion/unit-conversion.service';
import { GlobalIcon } from '../../icon/icon.constants';
import { ChartData } from '../model/chart-data';
import { ChartSeriesData } from '../model/chart-series-data';
import { ChartSeriesType } from '../model/chart-series-type';
import { ChartTitlePosition } from '../model/chart-title-position';
import { YAxis } from '../model/y-axis';
import { YAxisData } from '../model/y-axis-data';
import { YAxisNamePosition } from '../model/y-axis-name-position';

@Component({
  selector: 'app-chart-base',
  templateUrl: './chart-base.component.html',
  styleUrls: ['./chart-base.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class ChartBaseComponent implements OnInit, OnDestroy {
  @Input() animated: boolean = false;
  @Input() chartName: string;
  @Input() chartId: string;
  @Input() presentationView: boolean = false;
  @Input() set data(chartData: ChartData) {
    this._chartData = chartData;
    if (this._chartData) {
      this.years = this._getYears(this.data);
      this.legendNames = this.getLegendNames(this.data);
    }
    this.cdr.markForCheck();
  }
  get data(): ChartData {
    return this._chartData;
  }
  @HostListener('window:resize', ['$event'])
  onResize(event: Event) {
    this._resizeSubject.next(event);
  }

  protected chart: echarts.ECharts;
  protected legendNames: string[];
  protected _options: EChartsOption;
  protected years: number[];

  readonly DOWNLOAD_ICON = GlobalIcon.Download;
  readonly EXPORT_STRING = UI_STRINGS.GLOBAL.EXPORT_DIAGRAM;

  private _chartData: ChartData;
  private _resizeSubject: Subject<Event> = new Subject();

  private _drillDownEmissionColor = '#73808C';
  private _drillDownTextColor = '#FCFDFD';
  private _drillDownLineColor = '#E6E8EB';

  get leverInfoTransform(): SafeStyle {
    return this._sanitizer.bypassSecurityTrustStyle('');
  }

  get leverValue(): string {
    return '';
  }

  get options(): EChartsOption {
    const newOptions = this.buildOptions();

    if (!deepEqual(this._options, newOptions)) {
      this._options = newOptions;
    }
    return this._options;
  }

  get buttonStyle(): { [key: string]: string } {
    return {
      top: this._gridTop - 32 + 'px',
      right: 50 + 'px',
    };
  }

  get showLeverInfo(): boolean {
    return false;
  }

  private get _containLabel(): boolean {
    return this.data.grid?.containLabel ?? true;
  }

  private get _gridBottom(): number {
    //need a bit of bottom padding or else labels will get cut off.
    const bottomPadding = 3;
    let gridBottom: number = 0;
    if (this.data.grid?.bottom !== undefined) {
      gridBottom = this.data.grid.bottom;
    }
    return gridBottom + bottomPadding;
  }

  private get _gridTop(): number {
    let gridTop = 30;

    if (!this.data) {
      return gridTop;
    }

    if (!this.data.hideTitle && this.data.titlePosition === ChartTitlePosition.Top) {
      gridTop += 25;
    }
    if (this.showLegend()) {
      gridTop += 30;
    }
    return gridTop;
  }

  private get _legend() {
    if (!this.showLegend()) {
      return undefined;
    }
    const legendOptions: any = {
      data: this.legendNames,
      top: this._legendTop,
      selectedMode: false,
      x: 'center',
      padding: [2, 50],
      itemWidth: 10,
      itemHeight: 10,
    };

    if (this.presentationView) {
      legendOptions.textStyle = { color: this._drillDownTextColor };
    }

    return legendOptions;
  }

  private get _legendTop(): number {
    if (!this.data.hideTitle && this.data.titlePosition === ChartTitlePosition.Top) {
      return 30;
    } else {
      return 15;
    }
  }

  private get _showXAxisLine(): boolean {
    return this.data?.xAxis?.showAxisLine ?? false;
  }

  private get _showXAxisTick(): boolean {
    return this.data?.xAxis?.showAxisTick ?? false;
  }

  private get _title(): any {
    if (this.data.hideTitle) {
      return undefined;
    }
    const titleOptions: any = {
      text: this.data.title,
      left: 'center',
      textStyle: {
        fontFamily: 'IBM Plex Sans',
      },
    };
    if (this.data.titlePosition === ChartTitlePosition.Bottom) {
      titleOptions.top = 'bottom';
    } else {
      titleOptions.top = 0;
    }

    if (this.presentationView) {
      titleOptions.textStyle.color = this._drillDownTextColor;
    }

    return titleOptions;
  }

  private get _yAxes(): any[] {
    const yAxes: any[] = [this.buildYAxis(this.data.leftYAxis, YAxis.Left)];
    if (this.data.rightYAxis) {
      yAxes.push(this.buildYAxis(this.data.rightYAxis, YAxis.Right));
    }
    return yAxes;
  }

  constructor(
    protected cdr: ChangeDetectorRef,
    protected _sanitizer: DomSanitizer,
    protected translationService: TranslationService,
    protected formatNumberService: FormatNumberService,
    protected cccModelUtility: CCCModelUtility,
    protected _unitConversionService: UnitConversionService,
    @Inject(DOCUMENT) document: Document
  ) {
    //We rerender(call resize) when it becomes visible since the canvas might have been cleared by browser memory saving.
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') {
        this.chart?.resize();
      }
    });
  }

  ngOnInit(): void {
    this._resizeSubject.pipe(debounceTime(100)).subscribe((event) => {
      this.chart.resize();
    });
  }

  ngOnDestroy(): void {
    this._resizeSubject.unsubscribe();
  }

  onChartGlobalOut(event: any) {}

  onChartInit(chart: ECharts) {
    this.chart = chart;
  }

  onChartMouseDown(event: any) {}

  onChartMouseOut(event: any) {}

  onChartMouseOver(event: any) {}

  exportAsPng(): void {
    if (!this.chartName) {
      return;
    }
    const chartImg = this.chart.getDataURL({ type: 'png', backgroundColor: '#fff' });

    const link = document.createElement('a');
    link.href = chartImg;
    link.download = this.chartName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  protected buildBarBase(barSeries: ChartSeriesData): any {
    const barColour: string | string[] = barSeries.colour;
    const barBase = {
      type: 'bar',
      name: barSeries.name,
      stack: 'mystack',
      itemStyle: {
        normal: {
          color: barColour,
        },
        emphasis: {
          color: barColour,
        },
      },
      yAxisIndex: barSeries.yAxis === YAxis.Left ? 0 : 1,
      barWidth: '50%',
      cursor: 'auto',
      markLine: undefined,
      id: barSeries.id,
    };

    if (this.presentationView && barSeries.id > 0) {
      barBase.itemStyle.normal.color = this._drillDownEmissionColor;
      barBase.itemStyle.emphasis.color = this._drillDownEmissionColor;
    }

    return barBase;
  }

  protected buildLineBase(lineSeries: ChartSeriesData): any {
    const lineColour: string | string[] = lineSeries.colour;
    return {
      type: 'line',
      name: lineSeries.name,
      itemStyle: {
        normal: {
          color: lineColour,
        },
        emphasis: {
          color: lineColour,
        },
      },
      yAxisIndex: lineSeries.yAxis === YAxis.Left ? 0 : 1,
      cursor: 'auto',
      id: lineSeries.id,
      connectNulls: true,
    };
  }

  protected buildOptions(): EChartsOption {
    return this.data
      ? {
          animation: this.animated,
          title: this._title,
          legend: this._legend,
          xAxis: {
            data: this.years,
            axisLabel: {
              rotate: 90,
              color: this.presentationView ? this._drillDownTextColor : '',
            },
            axisTick: {
              show: this._showXAxisTick,
            },
            axisLine: {
              show: this._showXAxisLine,
            },
            z: 10,
          },
          yAxis: this._yAxes,
          grid: {
            top: this.data.grid?.top || this._gridTop,
            bottom: this._gridBottom,
            left: 70,
            right: 50,
            containLabel: this._containLabel,
          },
          series: this.getSeries(),
          animationDelayUpdate: this.animated
            ? function (idx) {
                return idx * 25;
              }
            : undefined,
        }
      : null;
  }

  protected buildYAxis(yAxisData: YAxisData, yAxis: YAxis): any {
    const formatter = yAxis === YAxis.Left ? this.getLeftYAxisFormatter(yAxisData) : this.getRightYAxisFormatter();
    const yAxisDataObject: any = {
      max: yAxisData.max,
      min: yAxisData.min,
      splitLine: {
        show: !yAxisData.hideLines,
      },
      axisLine: {
        show: yAxisData.showAxisLine ?? false,
      },
      axisTick: {
        show: yAxisData.showAxisTick ?? false,
      },
      axisLabel: {
        textStyle: {
          color: this.presentationView ? this._drillDownTextColor : (yAxisData.textStyleColor ?? '#999'),
        },
        formatter: formatter,
        showMaxLabel: yAxis === YAxis.Left,
      },
      nameLocation: yAxisData.nameData?.namePosition === YAxisNamePosition.Center ? 'center' : 'end',
      nameGap: yAxisData.nameData?.namePosition === YAxisNamePosition.Center ? 60 : 15,
      nameTextStyle: {
        fontFamily: 'IBM Plex Sans',
        color: this.presentationView ? this._drillDownTextColor : '',
      },
      name: yAxisData.nameData?.name,
    };

    if (this.presentationView) {
      yAxisDataObject.splitLine.lineStyle = { color: this._drillDownLineColor };
    }

    return yAxisDataObject;
  }

  protected getLeftYAxisFormatter(yAxis: YAxisData) {
    const formatter = (value, index) => {
      if (yAxis.unit === ChartUnit.none) {
        return this._localizeNumber(value);
      } else {
        return `${this._localizeNumber(value)} ${this.translate(chartUnitToUIString(yAxis.unit))}`;
      }
    };
    return formatter;
  }

  protected getLegendNames(data: ChartData): string[] {
    return data.series
      .map((s) => s.name)
      .filter((legend) => legend !== this.translate(UI_STRINGS.TRANSITION_TARGET_EDITOR.SERIES_TRANSITIONED_NAME));
    // TODO 2021-01-07: Filter is a temporary fix to hide transitioned emissions.
    // Solution is to not calculate remaining and transitioned separately anymore. Currently they are still separate bars but transitioned legend does not show
  }

  protected getLineData(l: ChartSeriesData) {
    return this.years.map((y) => ({ value: l.values.find((v) => v.year === y)?.value, symbol: 'circle' }));
  }

  protected getRightYAxisFormatter() {
    const formatter = (value, index) => {
      const decimals = numberOfDecimalsFromValue(value);
      const rounded = round(value, decimals);
      return Math.abs(rounded) >= 1000000 ? this._formatToShortNumber(rounded) : this._localizeNumber(rounded);
    };
    return formatter;
  }

  protected getSeries(): any[] {
    const bars: any[] = this.data.series
      .filter((s) => s.type === ChartSeriesType.Bar)
      .map((b) => {
        return {
          ...this.buildBarBase(b),
          data: b.values.map((d) => {
            return { value: d.value };
          }),
        };
      });
    const lines: any[] = this.data.series
      .filter((s) => s.type === ChartSeriesType.Line)
      .map((l) => {
        return { ...this.buildLineBase(l), data: this.getLineData(l) };
      });
    return bars.concat(lines);
  }

  protected showLegend(): boolean {
    return this.data.showLegend && !!this.legendNames;
  }

  protected translate(stringId: string): string {
    return this.translationService.translate(this.data.lang, stringId);
  }

  private _formatToShortNumber(value: number): string {
    return this.translationService.formatWithCompactNotation(this.data.lang, value);
  }

  private _getYears(data: ChartData): number[] {
    //for now assume that series[0] has a contiguous series with respect to years
    return data.series[0].values.map((v) => {
      return v.year;
    });
  }

  private _localizeNumber(value: number): string {
    return this.formatNumberService.formatWithBoardLanguage(this.data.lang, value);
  }
}
